/**
 * Centralized environment configuration
 * This file manages environment-specific settings to ensure consistency between dev and prod
 */

import { getDevSiteOrigin } from '../utils/constants/dev-site-origin';

// import.meta.env is injected by Vite; esbuild Node/Lambda bundles may omit it — always optional-chain.
export const ENV_CONFIG = {
  // Sentry Configuration
  SENTRY: {
    // Enable Sentry in both environments for consistent error tracking
    ENABLED: true,
    ENVIRONMENT: (import.meta.env?.PROD || process.env.NODE_ENV === 'production') ? 'production' : 'development',
    DEBUG: Boolean(import.meta.env?.DEV) || process.env.NODE_ENV !== 'production',
  },
  
  // Redux Configuration
  REDUX: {
    // Enable DevTools in both environments for consistent debugging
    DEVTOOLS_ENABLED: true,
  },
  
  // Error Handling
  ERROR_HANDLING: {
    // Show detailed error messages in both environments
    SHOW_DETAILED_ERRORS: true,
    // Include stack traces in development
    INCLUDE_STACK_TRACE: Boolean(import.meta.env?.DEV) || process.env.NODE_ENV !== 'production',
  },
  
  // URLs
  URLS: {
    BASE_URL: (import.meta.env?.PROD || process.env.NODE_ENV === 'production') ? 'https://www.jobcrawls.com' : 'http://localhost:3000',
    DEV_URL: 'http://localhost:3000',
    PROD_URL: 'https://www.jobcrawls.com',
  },
  
  // Logging
  LOGGING: {
    // Enable detailed logging in both environments
    LEVEL: 'info',
    // Include debug information
    DEBUG_ENABLED: true,
  },
  
  // Feature Flags
  FEATURES: {
    // Enable all features in both environments
    SENTRY_ERROR_TRACKING: true,
    REDUX_DEVTOOLS: true,
    DETAILED_ERROR_MESSAGES: true,
    DEBUG_MODE: true,
    // Map view is always enabled
    MAP_VIEW_ENABLED: true,
  },
} as const;

// Helper functions
export const isProduction = () => Boolean(import.meta.env?.PROD) || process.env.NODE_ENV === 'production';
export const isDevelopment = () => Boolean(import.meta.env?.DEV) || process.env.NODE_ENV !== 'production';

// Environment-specific URL helper
export const getBaseUrl = () =>
  import.meta.env?.PROD || process.env.NODE_ENV === 'production'
    ? ENV_CONFIG.URLS.PROD_URL
    : getDevSiteOrigin();

// Error message helper
export const getErrorMessage = (error: Error) => {
  if (ENV_CONFIG.ERROR_HANDLING.SHOW_DETAILED_ERRORS) {
    return error.message || "Something went wrong";
  }
  return "Something went wrong";
};

// Debug helper
export const debugLog = (message: string, data?: any) => {
  if (ENV_CONFIG.LOGGING.DEBUG_ENABLED) {
    console.log(`[${ENV_CONFIG.SENTRY.ENVIRONMENT.toUpperCase()}] ${message}`, data);
  }
}; 